import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  getCategories,
  getProducts,
  resolveMediaUrl,
  resolveProductCardImage,
} from '../services/productsApi';

const CATEGORY_IMAGE_MAP = {
  'Lounge': '/assets/Images/Loop lounge.jpg',
  'Mobiliario de Oficina': '/assets/Logos/mobiliario_oficina.jpg',
  'Multipropósito': '/assets/Logos/zona_comercial.jpg',
  'Sillas Ejecutivas': '/assets/Logos/sillas_ejecutivas.png',
  'Estaciones de Trabajo': '/assets/Logos/mesas_trabajo.jpg',
  'Mesas de Trabajo': '/assets/Logos/mesas_trabajo.jpg',
  'Sofás': '/assets/Logos/sofas_bancos_card.jpg',
  'Bancos': '/assets/Logos/bancos.jpg',
  Lockers: '/assets/Images/lockers.jpg',
  lockers: '/assets/Images/lockers.jpg',
};

const getDisplayCategoryName = (name) =>
  name === 'Mesas de Trabajo' ? 'Estaciones de Trabajo' : name;

const PRODUCTS_PER_PAGE = 28;
const PRODUCTS_INTRO_SRC = '/assets/Videos/products.mp4?v=3';
const PRODUCT_ORDER_SEED = 20260928;

const shuffleProducts = (items, seed) => {
  const copy = [...items];
  let state = seed >>> 0;
  const next = () => {
    state = (Math.imul(1664525, state) + 1013904223) >>> 0;
    return state / 4294967296;
  };

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(next() * (index + 1));
    [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
  }

  return copy;
};

const ProductsList = ({ onSelectProduct, initialNavigation }) => {
  const [categoriesData, setCategoriesData] = useState([]);
  const [productsData, setProductsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [selectedParentCategory, setSelectedParentCategory] = useState(null); // category object
  const [selectedCategory, setSelectedCategory] = useState(null); // category object
  const [currentPage, setCurrentPage] = useState(1);
  const skipPageResetRef = useRef(false);
  const restoreProductIdRef = useRef(null);
  const [introFinished, setIntroFinished] = useState(() =>
    Boolean(
      initialNavigation?.parentId ||
        initialNavigation?.categoryId ||
        initialNavigation?.productId
    )
  );

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setIsLoading(true);
      setApiError('');

      try {
        const [categories, products] = await Promise.all([
          getCategories(),
          getProducts(),
        ]);
        if (!mounted) return;
        setCategoriesData(categories || []);
        setProductsData(products || []);
      } catch (error) {
        if (!mounted) return;
        setApiError(
          'No se pudo cargar el catálogo desde la base de datos.'
        );
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  const productsByCategorySlug = useMemo(() => {
    return productsData.reduce((acc, product) => {
      const key = product.category_slug || 'sin-categoria';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(product);
      return acc;
    }, {});
  }, [productsData]);

  const categoriesById = useMemo(() => {
    return categoriesData.reduce((acc, category) => {
      acc[String(category.id)] = category;
      return acc;
    }, {});
  }, [categoriesData]);

  const rootCategories = useMemo(() => {
    return categoriesData.filter((category) => !category.parent_id);
  }, [categoriesData]);

  const categories = useMemo(() => {
    return rootCategories.map((category) => {
      const ownProducts = productsByCategorySlug[category.slug] || [];
      const children = categoriesData.filter(
        (item) => String(item.parent_id) === String(category.id)
      );
      const childrenCount = children.reduce(
        (sum, child) => sum + (productsByCategorySlug[child.slug]?.length || 0),
        0
      );
      const count = ownProducts.length + childrenCount;

      const previewImage =
        CATEGORY_IMAGE_MAP[category.name] ||
        ownProducts[0]?.cover_image ||
        children
          .map((child) => productsByCategorySlug[child.slug]?.[0]?.cover_image)
          .find(Boolean) ||
        '/assets/Images/inicio.jpg';

      return {
        id: category.id,
        name: category.name,
        slug: category.slug,
        children,
        count,
        previewImage,
      };
    });
  }, [categoriesData, productsByCategorySlug, rootCategories]);

  useEffect(() => {
    if (!initialNavigation || categoriesData.length === 0) return;

    const { parentId, categoryId, page, productId } = initialNavigation;
    skipPageResetRef.current = true;
    restoreProductIdRef.current = productId || null;

    if (parentId || categoryId || productId) {
      setIntroFinished(true);
    }

    if (parentId) {
      const parent = categoriesData.find(
        (item) => String(item.id) === String(parentId)
      );
      if (parent) {
        setSelectedParentCategory(parent);
      }
    }

    if (categoryId) {
      const category = categoriesData.find(
        (item) => String(item.id) === String(categoryId)
      );
      if (category) {
        setSelectedCategory(category);
      }
    }

    if (page && Number(page) > 0) {
      setCurrentPage(Number(page));
    }
  }, [categoriesData, initialNavigation]);

  useEffect(() => {
    if (initialNavigation) return;
    if (categoriesData.length === 0 || selectedCategory || selectedParentCategory) {
      return;
    }

    let slug = '';
    try {
      slug = window.sessionStorage.getItem('verissa-home-category-slug') || '';
      if (slug) {
        window.sessionStorage.removeItem('verissa-home-category-slug');
      }
    } catch {
      return;
    }

    if (!slug) return;

    const category = categoriesData.find((item) => item.slug === slug);
    if (!category) return;

    if (category.parent_id) {
      const parent = categoriesData.find(
        (item) => String(item.id) === String(category.parent_id)
      );
      if (parent) setSelectedParentCategory(parent);
      setSelectedCategory(category);
      setIntroFinished(true);
      return;
    }

    const children = categoriesData.filter(
      (item) => String(item.parent_id) === String(category.id)
    );
    if (children.length > 0) {
      setSelectedParentCategory(category);
      setSelectedCategory(null);
      setIntroFinished(true);
      return;
    }

    setSelectedCategory(category);
    setIntroFinished(true);
  }, [categoriesData, selectedCategory, selectedParentCategory, initialNavigation]);

  const subcategories = useMemo(() => {
    if (!selectedParentCategory) return [];
    const parent = categoriesById[String(selectedParentCategory.id)];
    if (!parent) return [];

    const children = categoriesData.filter(
      (item) => String(item.parent_id) === String(parent.id)
    );

    return children.map((child) => {
      const childProducts = productsByCategorySlug[child.slug] || [];
      return {
        id: child.id,
        slug: child.slug,
        name: child.name,
        count: childProducts.length,
        previewImage:
          CATEGORY_IMAGE_MAP[child.name] ||
          childProducts[0]?.cover_image ||
          '/assets/Images/inicio.jpg',
      };
    });
  }, [categoriesById, categoriesData, productsByCategorySlug, selectedParentCategory]);

  const filteredProducts = useMemo(() => {
    if (!selectedCategory) return [];
    const items = productsByCategorySlug[selectedCategory.slug] || [];
    return shuffleProducts(items, PRODUCT_ORDER_SEED);
  }, [productsByCategorySlug, selectedCategory]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE));
  }, [filteredProducts.length]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [currentPage, filteredProducts]);

  useEffect(() => {
    if (skipPageResetRef.current) {
      skipPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [selectedParentCategory, selectedCategory]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    const productId = restoreProductIdRef.current;
    if (!productId || isLoading || !selectedCategory) return;

    const onPage = paginatedProducts.some(
      (product) => String(product.id) === String(productId)
    );
    if (!onPage) return;

    const timer = window.setTimeout(() => {
      const card = document.getElementById(`product-card-${productId}`);
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      restoreProductIdRef.current = null;
    }, 80);

    return () => window.clearTimeout(timer);
  }, [isLoading, selectedCategory, currentPage, paginatedProducts]);

  const handleBack = () => {
    if (selectedCategory) {
      setSelectedCategory(null);
      return;
    }
    setSelectedParentCategory(null);
  };

  const handleCategoryClick = (category) => {
    if (category.children?.length > 0) {
      setSelectedParentCategory(category);
      setSelectedCategory(null);
      return;
    }
    setSelectedCategory(category);
  };

  const showingCategories = !selectedParentCategory && !selectedCategory;
  const showingSubcategories = !!selectedParentCategory && !selectedCategory;

  const renderPagination = (position) => {
    if (filteredProducts.length <= PRODUCTS_PER_PAGE) return null;

    return (
      <div className={`products-pagination products-pagination-${position}`}>
        {Array.from({ length: totalPages }).map((_, index) => {
          const page = index + 1;
          return (
            <button
              key={`${position}-${page}`}
              type="button"
              className={`products-page-btn ${
                page === currentPage ? 'active' : ''
              }`}
              onClick={(event) => {
                event.currentTarget.blur();
                setCurrentPage(page);
                window.requestAnimationFrame(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                });
              }}
            >
              {page}
            </button>
          );
        })}
      </div>
    );
  };
  const showProductsIntro = showingCategories && !introFinished;

  if (showProductsIntro) {
    return (
      <section className="products-page products-page--intro" id="productos">
        <div className="products-intro">
          <video
            className="products-intro-video"
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={() => setIntroFinished(true)}
            aria-label="Introducción a productos"
          >
            <source src={PRODUCTS_INTRO_SRC} type="video/mp4" />
          </video>
          <button
            type="button"
            className="products-intro-skip"
            onClick={() => setIntroFinished(true)}
          >
            Saltar
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="products-page" id="productos">
      <div className="products-header">
        {!showingCategories ? (
          <>
            <button
              type="button"
              className="products-categories-back-btn"
              onClick={handleBack}
            >
              ← {showingSubcategories ? 'Ver categorías' : 'Ver subcategorías'}
            </button>
            <h1 className="section-title">
              {getDisplayCategoryName(
                showingSubcategories
                  ? selectedParentCategory?.name
                  : selectedCategory?.name
              )}
            </h1>
            <p className="section-subtitle">
              {showingSubcategories
                ? 'Selecciona una subcategoría para continuar.'
                : 'Explora los productos de esta categoría y selecciona el que te interesa.'}
            </p>
          </>
        ) : (
          <>
            <h1 className="section-title">Productos</h1>
            <p className="section-subtitle">
              Selecciona una categoría para explorar su catálogo.
            </p>
          </>
        )}
      </div>

      {isLoading && (
        <p className="section-subtitle">Cargando catálogo...</p>
      )}

      {!isLoading && apiError && (
        <p className="section-subtitle">{apiError}</p>
      )}

      {!isLoading && !apiError && showingCategories ? (
        <div className="product-categories-grid">
          {categories.map((category, index) => (
            <motion.button
              key={category.name}
              type="button"
              className="product-category-card"
              onClick={() => handleCategoryClick(category)}
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.07 }}
              whileHover={{ y: -10 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="product-category-image-wrapper">
                <img
                  src={category.previewImage}
                  alt={category.name}
                  className="product-category-image"
                  loading="lazy"
                  decoding="async"
                />
                <div className="product-category-shine" />
                <div className="product-category-overlay" />
              </div>
              <div className="product-category-content">
                <div className="product-category-meta-row">
                  <h3 className="product-category-title">{getDisplayCategoryName(category.name)}</h3>
                  <span className="product-category-arrow" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="19" x2="19" y2="5"></line>
                      <polyline points="9 5 19 5 19 15"></polyline>
                    </svg>
                  </span>
                </div>
                <span className="product-category-cta">
                  {category.children?.length
                    ? 'Ver subcategorías'
                    : 'Explorar categoría'}
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      ) : !isLoading && !apiError && showingSubcategories ? (
        <div className="product-categories-grid">
          {subcategories.map((subcategory, index) => (
            <motion.button
              key={subcategory.name}
              type="button"
              className="product-category-card"
              onClick={() => setSelectedCategory(subcategory)}
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.07 }}
              whileHover={{ y: -10 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="product-category-image-wrapper">
                <img
                  src={subcategory.previewImage}
                  alt={subcategory.name}
                  className="product-category-image"
                  loading="lazy"
                  decoding="async"
                />
                <div className="product-category-shine" />
                <div className="product-category-overlay" />
              </div>
              <div className="product-category-content">
                <div className="product-category-meta-row">
                  <h3 className="product-category-title">{getDisplayCategoryName(subcategory.name)}</h3>
                  <span className="product-category-arrow" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="19" x2="19" y2="5"></line>
                      <polyline points="9 5 19 5 19 15"></polyline>
                    </svg>
                  </span>
                </div>
                <span className="product-category-cta">Explorar productos</span>
              </div>
            </motion.button>
          ))}
        </div>
      ) : !isLoading && !apiError ? (
        <>
          {renderPagination('top')}
          <div className="products-grid">
          {filteredProducts.length > 0 ? (
            paginatedProducts.map((product) => (
              <motion.button
                key={product.id}
                id={`product-card-${product.id}`}
                type="button"
                className="product-card"
                onClick={() =>
                  onSelectProduct({
                    ...product,
                    category: product.category,
                    images: product.cover_image ? [product.cover_image] : [],
                    _navigation: {
                      parentId: selectedParentCategory?.id || null,
                      categoryId: selectedCategory?.id || null,
                      productId: product.id,
                      page: currentPage,
                    },
                  })
                }
                whileHover={{ y: -8, boxShadow: '0 18px 45px rgba(0,0,0,0.12)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="product-image-wrapper">
                  <img
                    src={
                      resolveProductCardImage(product.cover_image, product) ||
                      '/assets/Images/inicio.jpg'
                    }
                    alt={product.name}
                    className="product-image"
                    loading="lazy"
                    decoding="async"
                    onError={(event) => {
                      const fallback =
                        resolveMediaUrl(product.cover_image) ||
                        '/assets/Images/inicio.jpg';
                      if (event.currentTarget.src !== fallback) {
                        event.currentTarget.src = fallback;
                      }
                    }}
                  />
                </div>
                <div className="product-content">
                  <span className="product-category">{getDisplayCategoryName(product.category)}</span>
                  <h3 className="product-title">{product.name}</h3>
                </div>
              </motion.button>
            ))
          ) : (
            <p className="section-subtitle">
              Aún no hay productos disponibles en esta subcategoría.
            </p>
          )}
          </div>
          {renderPagination('bottom')}
        </>
      ) : null}
    </section>
  );
};

export default ProductsList;

